import { useCallback, useRef, useState } from "react";

import ParseJSONService from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/jsonParserService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import ParseEventStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/streamParserService";
import type { StreamEvent } from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/types";
import type { AiAgentEmailPayload } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import { aiAgentQueryKeys } from "./queryKeyFactory";
import { useSSEStream } from "./useSSEStream";

const useCreateEmailThreadQuery = (
    payload: AiAgentEmailPayload | null,
    queryKeyProp?: readonly string[],
    onComplete?: (threadId: string, emailPayload: string) => void,
) => {
    const [threadId, setThreadId] = useState("");
    const threadIdRef = useRef("");
    const [messageId, setMessageId] = useState("");
    const [email, setEmail] = useState<ParsedAIEmailPreview | null>(null);
    const emailRef = useRef<string>("");

    const [emailPreviews, setEmailPreviews] = useState<ParsedAIEmailPreview[]>([]);

    const resetState = useCallback(() => {
        threadIdRef.current = "";
        emailRef.current = "";
        setThreadId("");
        setMessageId("");
        setEmail(null);
        setEmailPreviews([]);
    }, []);

    const handleStreamEvent = useCallback(
        (event: StreamEvent) => {
            if (ParseEventStreamService.isInfoStreamEvent(event)) {
                threadIdRef.current = event.data.threadId;
                setThreadId(event.data.threadId);
            }

            if (ParseEventStreamService.isEmailStreamEvent(event)) {
                const chunk = event.data[0].content;
                emailRef.current = chunk;
                const id = event.data[0].id;
                if (id && !messageId) {
                    setMessageId(id);
                }
                const parsedEmail = ParseJSONService.parseEmail(chunk);
                const { cleaned, json, index } = ParseJSONService.extractEmbeddedJSON(parsedEmail.body);

                const preview: ParsedAIEmailPreview = {
                    subject: parsedEmail.subject,
                    body: cleaned,
                    chartJson: null,
                    chartAnchorIndex: -1,
                };
                if (json && typeof json === "object" && json !== null) {
                    preview.chartJson = json as Record<string, unknown>;
                    preview.chartAnchorIndex = index;
                }
                setEmail(preview);
            }

            if (ParseEventStreamService.isEmailPreviewStreamEvent(event)) {
                const chunk = event.data[0].content;
                const parsedEmailPreviews = ParseJSONService.parseAIEmailPreviews(chunk);
                setEmailPreviews(parsedEmailPreviews.filter((p) => p.subject && p.body));
            }

            if (ParseEventStreamService.isUnknownStreamEvent(event)) {
                setEmail({
                    subject: `${event.data.error}: ${event.data.message}`,
                    body: "",
                    chartJson: null,
                    chartAnchorIndex: -1,
                });
                emailRef.current = "";
            }
        },
        [messageId],
    );

    const queryKey =
        queryKeyProp ??
        aiAgentQueryKeys.generateEmailThread(
            payload?.domain ?? "",
            payload?.emailContext.attendees ?? "",
            payload?.emailContext.isCompanyLevel ?? false,
        );

    const { isFetching, stop, refetch } = useSSEStream({
        enabled: !!payload,
        queryKey,
        payload,
        fetcher: salesApiService.aiAgent.email.createAIEmailAgentThread,
        onEvent: handleStreamEvent,
        onComplete: () => {
            void quotaQueryClient.invalidateDataCreditsState();
            onComplete?.(threadIdRef.current, emailRef.current);
        },
    });

    return {
        threadId,
        messageId,
        email,
        emailPreviews,
        onSetEmailPreviews: setEmailPreviews,
        isLoading: isFetching,
        stop,
        reset: resetState,
        refetch,
    };
};

export default useCreateEmailThreadQuery;
